import { useState } from 'react';
import type { FC } from 'react';
import classnames from 'classnames';

import type { EventTreeChangeAction, EventTreeSerieValue } from 'app/pages/EventsPage/types';
import type { CheckboxStatus } from 'app/components/Checkbox';
import Checkbox from 'app/components/Checkbox';
import TierIndicator from 'app/components/TierIndicator';
import Button from 'app/components/Button/ResetedButton';
import Icon from 'app/components/Icon';
import arrowIcon from 'app/icons/arrow.svg';
import EventTreeTournament from 'app/pages/EventsPage/EventsSidebar/EventTree/EventTreeVideogame/EventTreeTournament';

import style from './style.scss';

interface Props {
  value: EventTreeSerieValue;
  dispatchChange: (action: EventTreeChangeAction) => void;
}

function getCheckboxStatus(value: EventTreeSerieValue): CheckboxStatus {
  if (value.selected) {
    return 'checked';
  }

  const someTournamentsSelected = value.tournaments.some(t => t.selected);
  if (someTournamentsSelected) {
    return 'indeterminate';
  }

  return 'unchecked';
}

const EventTreeSerie: FC<Props> = ({ value, dispatchChange }) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded(e => !e);

  const handleChange = () => {
    dispatchChange({ type: 'EVENT_TREE_TOGGLE_SERIE', payload: value.id });
  };

  const checkboxStatus = getCheckboxStatus(value);

  const arrowClassName = classnames(style.arrow, {
    [style.arrow_expanded]: expanded
  });

  return (
    <div>
      <div className={style.line}>
        <label className={style.label}>
          <Checkbox
            className={style.checkbox}
            status={checkboxStatus}
            size="small"
            disabled={value.tournaments.length === 0}
            onChange={handleChange}
          />
          {value.tier && <TierIndicator className={style.tier} tier={value.tier} />}
          <span className={style.serieName} title={value.label}>
            {value.label}
          </span>
        </label>
        {value.tournaments.length > 0 && (
          <Button className={style.expandButton} onClick={toggleExpanded}>
            <Icon className={arrowClassName} href={arrowIcon} />
          </Button>
        )}
      </div>
      {expanded && (
        <div className={style.tournamentsContainer}>
          {value.tournaments.map(tournament => (
            <EventTreeTournament
              key={tournament.id}
              value={tournament}
              dispatchChange={dispatchChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

EventTreeSerie.displayName = 'EventTreeSerie';

export default EventTreeSerie;
